import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { toast } from "sonner";

/**
 * Context for preloading and managing VAD (Voice Activity Detection) and WebSpeech API.
 *
 * This initializes voice input services at app startup for instant availability
 * when the user navigates to the voice chat page.
 *
 * Benefits:
 * - Instant microphone readiness (no loading delay)
 * - Centralized error handling with user-friendly messages
 * - Reinitialize capability for error recovery
 */

interface VoiceInputContextValue {
  /** True if VAD/WebSpeech are currently initializing */
  isInitializing: boolean;

  /** Error that occurred during initialization, if any */
  initError: Error | null;

  /** True if initialization completed successfully */
  isReady: boolean;

  /** Attempt to reinitialize voice input services */
  reinitialize: () => Promise<void>;
}

const VoiceInputContext = createContext<VoiceInputContextValue | undefined>(undefined);

export function VoiceInputProvider({ children }: { children: ReactNode }) {
  const [isInitializing, setIsInitializing] = useState(true);
  const [initError, setInitError] = useState<Error | null>(null);
  const [isReady, setIsReady] = useState(false);

  const initialize = useCallback(async () => {
    setIsInitializing(true);
    setInitError(null);

    try {
      // Check browser support for WebSpeech API
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;

      if (!SpeechRecognition) {
        throw new Error("Speech recognition not supported in this browser");
      }

      // Note: VAD preloading is complex and depends on @ricky0123/vad-react
      // For now, we just validate WebSpeech support
      // Full VAD preloading can be added in a future enhancement

      setIsReady(true);
      setIsInitializing(false);
    } catch (error) {
      const err = error instanceof Error ? error : new Error("Unknown initialization error");
      setInitError(err);
      setIsReady(false);
      setIsInitializing(false);

      // Show user-friendly error message
      toast.error("Microphone Initialization Failed", {
        description: getErrorDescription(err),
        action: {
          label: "Retry",
          onClick: () => initialize(),
        },
        duration: 10000, // Show longer for errors
      });
    }
  }, []);

  const reinitialize = useCallback(async () => {
    await initialize();
  }, [initialize]);

  useEffect(() => {
    // Initialize on mount
    initialize();
  }, [initialize]);

  const value: VoiceInputContextValue = {
    isInitializing,
    initError,
    isReady,
    reinitialize,
  };

  return (
    <VoiceInputContext.Provider value={value}>
      {children}
    </VoiceInputContext.Provider>
  );
}

/**
 * Hook to access voice input context.
 *
 * @example
 * ```tsx
 * const { isReady, isInitializing, initError, reinitialize } = useVoiceInput();
 *
 * if (isInitializing) {
 *   return <Spinner>Loading microphone...</Spinner>;
 * }
 *
 * if (initError) {
 *   return <Error message={initError.message} onRetry={reinitialize} />;
 * }
 * ```
 */
export function useVoiceInput() {
  const context = useContext(VoiceInputContext);

  if (context === undefined) {
    throw new Error("useVoiceInput must be used within VoiceInputProvider");
  }

  return context;
}

/**
 * Get user-friendly error description based on error type
 */
function getErrorDescription(error: Error): string {
  const message = error.message.toLowerCase();

  if (message.includes("not supported")) {
    return "Your browser doesn't support voice input. Please use Chrome, Edge, or Safari.";
  }

  if (message.includes("permission") || message.includes("denied")) {
    return "Microphone access was denied. Please allow microphone access in your browser settings.";
  }

  if (message.includes("network")) {
    return "Network error. Please check your internet connection and try again.";
  }

  return `An error occurred: ${error.message}`;
}
